import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FormProvider } from '@subflow-frontend/components/hook-form';
import dayjs, { isDayjs } from 'dayjs';
import { useContext } from 'react';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import * as Yup from 'yup';
import { AgeGateContext } from './AgeGateContext';

export function AgeGateInput({ name, label }: { name: string; label: string }) {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              slotProps={{
                textField: {
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
              value={field.value}
              onChange={field.onChange}
              label={label}
            />
          </LocalizationProvider>
        );
      }}
    />
  );
}

interface FormValuesProps {
  birthDate: string;
}

export default function AgeGateModal() {
  const {
    product,
    ageRestrictionConfirmed,
    ageRestrictionValue,
    setAgeRestrictionConfirmed,
    setBirthDate,
  } = useContext(AgeGateContext);

  const defaultValues = {
    birthDate: null,
  };

  const AgeGateSchema = Yup.object().shape({
    birthDate: Yup.object()
      .nullable()
      .test('date-required', 'Date is required!', (value) => {
        if (isDayjs(value)) {
          return value.isValid();
        } else {
          return false;
        }
      })
      .test(
        'is-old-enough',
        `You must be at least ${ageRestrictionValue} years old to access this content.`,
        function (value) {
          if (isDayjs(value)) {
            const now = dayjs();
            const ageDiff = now.diff(value, 'year');
            return ageDiff >= ageRestrictionValue;
          } else {
            return false;
          }
        }
      ),
  });
  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(AgeGateSchema),
    defaultValues,
  });

  const { handleSubmit } = methods;

  if (
    !product ||
    !product.ageRestrictionActive ||
    !product.ageRestrictionValue
  ) {
    return null;
  }

  const onSubmit = ({ birthDate }: any) => {
    setBirthDate(birthDate);
    setAgeRestrictionConfirmed(true);
  };

  return (
    <Dialog
      sx={{
        backdropFilter: 'blur(50px)',
      }}
      disablePortal
      fullWidth={true}
      open={!ageRestrictionConfirmed}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Enter your date of birth</DialogTitle>
        <DialogContent>
          <Stack pt={2} gap={2}>
            <Typography>
              You must be at least {ageRestrictionValue} years old to access the
              following content
            </Typography>

            <AgeGateInput name="birthDate" label="Birth Date" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" type={'submit'}>
            Confirm
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
